/**
 * Q1 — "How strong is my brand, really?"
 *
 * Two-card layout: overall Brand Equity (left) + the 7 Brand Boosters bar
 * list (right). Boosters are clickable to open the drawer.
 */

import React from 'react';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import type { MarketerViewData, ScoreNode } from '../types';
import { BAND_PALETTE, formatBipolarScore } from '../config/bandColours';
import { getElementColour } from '../config/elementColours';
import { THEME } from '../config/theme';
import { BandTag } from '../atoms';
import { Card } from '../atoms';
import { Eyebrow } from '../atoms';
import { EquityArcGauge } from '../atoms';
import { SignalBar } from '../atoms';
import { SkeletonLines } from '../atoms';
import { SectionHead } from './SectionHead';

/** One cell of the Overall-BE stat strip (matches hand-off `.snap-cell`). */
const SnapCell: React.FunctionComponent<{
  label: React.ReactNode;
  up?: boolean;
  first?: boolean;
  children: React.ReactNode;
}> = ({ label, up, first, children }) => (
  <div
    style={{
      padding: '13px 15px',
      borderLeft: first ? 'none' : `1px solid ${THEME.borderSubtle}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 5,
    }}
  >
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: THEME.textMuted,
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: 15,
        fontWeight: 700,
        color: up ? '#059669' : THEME.textBase,
        display: 'flex',
        alignItems: 'center',
        gap: 7,
      }}
    >
      {children}
    </span>
  </div>
);

export interface Q1Props {
  data: MarketerViewData;
  onOpen: (node: ScoreNode) => void;
  sevenName: string;
  /** True while the AI boosters summary is still generating — skeleton the
   *  caption instead of flashing the deterministic footnote (which reads as a
   *  finished take). Falls through to the footnote once loaded or if absent. */
  boostersSummaryLoading?: boolean;
}

export const Q1: React.FunctionComponent<Q1Props> = ({
  data,
  onOpen,
  sevenName,
  boostersSummaryLoading,
}) => {
  const benchmark = data.brand.benchmarkComparison;
  const vsPrevious = data.brand.vsPrevious;
  const snapCols = 1 + (vsPrevious != null ? 1 : 0) + (benchmark ? 1 : 0);
  // "vs Previous" compares against the equivalent window immediately before the
  // current one — surface that window (e.g. "Last 60 days" → "prior 60 days").
  const previousWindow = data.brand.period.replace(/^last\s+/i, '');
  return (
    <section id="strong" style={{ paddingTop: 50 }}>
      <SectionHead
        q="Q1"
        title={
          <FormattedMessage
            id="marketerView.q1.title"
            defaultMessage="How strong is my brand, really?"
          />
        }
        sub={
          <FormattedMessage
            id="marketerView.q1.sub"
            defaultMessage="One number rolls up everything: a -100 to +100 Brand Equity score.{br}Below it sit {sevenName}: the seven things that shape how customers feel about you and what they buy."
            values={{ sevenName, br: <br /> }}
          />
        }
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '0.9fr 1.1fr',
          gap: 22,
        }}
      >
        {/* Overall BE */}
        <Card
          style={{
            background:
              'radial-gradient(130% 90% at 50% -10%, #FFF7E8 0%, rgba(255,247,232,0) 58%), #FFFFFF',
          }}
        >
          <Eyebrow>
            <FormattedMessage id="marketerView.briefing.overall" defaultMessage="Overall Brand Equity" />
          </Eyebrow>

          <div
            role="button"
            tabIndex={0}
            onClick={() => onOpen(data.equityNode)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen(data.equityNode);
              }
            }}
            style={{ cursor: 'pointer', borderRadius: 12 }}
          >
            <EquityArcGauge value={data.brand.equity}>
              <span
                style={{
                  fontSize: 58,
                  fontWeight: 800,
                  color: getElementColour('Brand Equity'),
                  letterSpacing: '-0.03em',
                  lineHeight: 1,
                }}
              >
                {formatBipolarScore(data.brand.equity)}
              </span>
              <BandTag
                classification={data.brand.equityClassification}
                band={data.brand.equityBand}
              />
            </EquityArcGauge>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              maxWidth: 318,
              margin: '2px auto 0',
              fontSize: 12,
              color: THEME.textMuted,
            }}
          >
            <span>
              <FormattedMessage id="marketerView.scale.weak" defaultMessage="Weak −100" />
            </span>
            <span style={{ color: THEME.textSecondary }}>0</span>
            <span>
              <FormattedMessage id="marketerView.scale.strong" defaultMessage="+100 Strong" />
            </span>
          </div>

          {/* Stat strip — Responses · vs Previous · vs Benchmark (hand-off `.snap`) */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${snapCols}, 1fr)`,
              marginTop: 20,
              border: `1px solid ${THEME.borderSubtle}`,
              borderRadius: 12,
              overflow: 'hidden',
              background: THEME.bgTinted,
            }}
          >
            <SnapCell
              first
              label={
                <FormattedMessage id="marketerView.q1.responses" defaultMessage="Responses" />
              }
            >
              <b style={{ fontSize: 19, fontWeight: 800 }}>
                {data.brand.totalResponses.toLocaleString()}
              </b>
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  color: THEME.textSecondary,
                  background: THEME.bgChip,
                  borderRadius: 5,
                  padding: '2px 7px',
                }}
              >
                {data.brand.period}
              </span>
            </SnapCell>
            {vsPrevious != null && (
              <SnapCell
                up={vsPrevious >= 0}
                label={
                  <FormattedMessage
                    id="marketerView.q1.vsPrevious"
                    defaultMessage="vs Previous"
                  />
                }
              >
                {vsPrevious >= 0 ? (
                  <ArrowUpOutlined style={{ fontSize: 12 }} />
                ) : (
                  <ArrowDownOutlined style={{ fontSize: 12, color: '#dc6803' }} />
                )}
                <span style={{ color: vsPrevious >= 0 ? '#059669' : '#dc6803' }}>
                  {vsPrevious >= 0 ? '+' : ''}
                  {vsPrevious.toFixed(1)}%
                </span>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    color: THEME.textSecondary,
                    background: THEME.bgChip,
                    borderRadius: 5,
                    padding: '2px 7px',
                  }}
                >
                  <FormattedMessage
                    id="marketerView.q1.priorWindow"
                    defaultMessage="prior {window}"
                    values={{ window: previousWindow }}
                  />
                </span>
              </SnapCell>
            )}
            {benchmark && (
              <SnapCell
                up={benchmark.diff >= 0}
                label={
                  <FormattedMessage
                    id="marketerView.q1.vsBenchmark"
                    defaultMessage="vs Benchmark"
                  />
                }
              >
                {benchmark.diff >= 0 ? (
                  <ArrowUpOutlined style={{ fontSize: 12 }} />
                ) : (
                  <ArrowDownOutlined style={{ fontSize: 12, color: '#dc6803' }} />
                )}
                <span style={{ color: benchmark.diff >= 0 ? '#059669' : '#dc6803' }}>
                  {benchmark.diff >= 0 ? '+' : ''}
                  {benchmark.diff.toFixed(1)}%
                </span>
              </SnapCell>
            )}
          </div>

          {boostersSummaryLoading ? (
            <SkeletonLines
              lines={2}
              height={12}
              gap={8}
              lastWidth="70%"
              style={{ marginTop: 18 }}
            />
          ) : (
            <p
              style={{
                fontSize: 14,
                lineHeight: 1.6,
                color: THEME.textSecondary,
                marginTop: 18,
              }}
            >
              {data.boostersFootnote}
            </p>
          )}
        </Card>

        {/* The 7 Boosters */}
        <Card>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 12,
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#94A3B8',
                whiteSpace: 'nowrap',
              }}
            >
              {sevenName}
            </span>
            <span
              style={{
                fontSize: 11,
                color: '#94A3B8',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              <FormattedMessage
                id="marketerView.q1.tapAny"
                defaultMessage="tap any element to open"
              />
            </span>
          </div>
          <div style={{ marginTop: 12 }}>
            {data.boosters.map((b) => (
              <div key={b.key}>
                <SignalBar node={b} onClick={() => onOpen(b)} />
              </div>
            ))}
          </div>
          <div
            style={{
              marginTop: 10,
              fontSize: 12.5,
              color: '#94A3B8',
              borderTop: '1px solid #E5E7EB',
              paddingTop: 12,
              lineHeight: 1.5,
            }}
          >
            {data.boosters
              .filter((b) => b.band === 'weak')
              .map((b) => (
                <span key={b.key}>
                  <b style={{ color: BAND_PALETTE.weak.fg }}>
                    {b.display}
                    {!b.locked ? ` (${formatBipolarScore(b.value)})` : ''}
                  </b>
                  {' '}
                </span>
              ))}
            {data.boosters.some((b) => b.value == null) && (
              <span>
                <b style={{ color: BAND_PALETTE.na.fg }}>
                  {data.boosters
                    .filter((b) => b.value == null)
                    .map((b) => b.display)
                    .join(', ')}
                </b>{' '}
                <FormattedMessage
                  id="marketerView.q1.unmeasuredSuffix"
                  defaultMessage="unmeasured."
                />
              </span>
            )}
          </div>
        </Card>
      </div>
    </section>
  );
};
